import { useState, useRef, type FormEvent } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { reportTestimonialFailure } from "@/lib/testimonial-alerts";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Quote, ShieldCheck } from "lucide-react";
import { nameField, emailField } from "@/lib/form-security";
import { trackEvent } from "@/lib/tracking";

const todayISO = () => new Date().toISOString().slice(0, 10);

const testimonialSchema = z
  .object({
    full_name: nameField,
    email: emailField,
    role_title: z.string().trim().max(160, "Role too long").optional().or(z.literal("")),
    organization: z.string().trim().max(160, "Organization too long").optional().or(z.literal("")),
    quote: z
      .string()
      .trim()
      .min(20, "Quotes should be at least 20 characters")
      .max(1200, "Please keep the quote under 1,200 characters"),
    context: z.string().trim().max(4000, "Context too long").optional().or(z.literal("")),
    rating: z
      .union([z.literal(""), z.coerce.number().int().min(1).max(5)])
      .optional(),
    release_effective_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a start date"),
    release_expires_at: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .or(z.literal("")),
    signature_name: z
      .string()
      .trim()
      .min(2, "Type your full name as your signature")
      .max(120, "Signature too long"),
    consent_publish_quote: z.boolean().refine((v) => v === true, {
      message: "We need permission to publish your quote",
    }),
    consent_publish_name: z.boolean(),
    consent_publish_org: z.boolean(),
    consent_use_marketing: z.boolean(),
  })
  .refine(
    (v) =>
      !v.release_expires_at || v.release_expires_at >= v.release_effective_date,
    { path: ["release_expires_at"], message: "End date must be on or after the start date" },
  );

type Errors = Partial<Record<keyof z.infer<typeof testimonialSchema>, string>>;

export default function TestimonialIntake() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const formRef = useRef<HTMLFormElement>(null);
  const startedAt = useRef(Date.now());

  const clearError = (k: keyof Errors) =>
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const fd = new FormData(e.currentTarget);
    if ((fd.get("website") as string)?.length) return;
    if (Date.now() - startedAt.current < 2500) {
      toast.error("Please take a moment to complete the form.");
      return;
    }

    const raw = {
      full_name: String(fd.get("full_name") ?? ""),
      email: String(fd.get("email") ?? ""),
      role_title: String(fd.get("role_title") ?? ""),
      organization: String(fd.get("organization") ?? ""),
      quote: String(fd.get("quote") ?? ""),
      context: String(fd.get("context") ?? ""),
      rating: String(fd.get("rating") ?? ""),
      release_effective_date: String(fd.get("release_effective_date") ?? ""),
      release_expires_at: String(fd.get("release_expires_at") ?? ""),
      signature_name: String(fd.get("signature_name") ?? ""),
      consent_publish_quote: fd.get("consent_publish_quote") === "on",
      consent_publish_name: fd.get("consent_publish_name") === "on",
      consent_publish_org: fd.get("consent_publish_org") === "on",
      consent_use_marketing: fd.get("consent_use_marketing") === "on",
    };

    const parsed = testimonialSchema.safeParse(raw);
    if (!parsed.success) {
      const flat: Errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof Errors;
        if (!flat[k]) flat[k] = issue.message;
      }
      setErrors(flat);
      void reportTestimonialFailure({
        reason: `Validation failed: ${Object.values(flat).slice(0, 3).join(" | ")}`,
        fields: Object.keys(flat),
        email: raw.email,
      });
      const firstKey = Object.keys(flat)[0];
      if (firstKey && formRef.current) {
        const el = formRef.current.querySelector<HTMLElement>(`[name="${firstKey}"]`);
        el?.focus();
      }
      return;
    }

    setSubmitting(true);
    try {
      const d = parsed.data;
      const payload = {
        full_name: d.full_name,
        email: d.email,
        role_title: d.role_title || null,
        organization: d.organization || null,
        quote: d.quote,
        context: d.context || null,
        rating: typeof d.rating === "number" ? d.rating : null,
        release_effective_date: d.release_effective_date,
        release_expires_at: d.release_expires_at || null,
        signature_name: d.signature_name,
        consent_publish_quote: d.consent_publish_quote,
        consent_publish_name: d.consent_publish_name,
        consent_publish_org: d.consent_publish_org,
        consent_use_marketing: d.consent_use_marketing,
        user_agent: navigator.userAgent.slice(0, 500),
      };
      const { error } = await supabase.from("testimonial_submissions").insert(payload);
      if (error) throw error;
      trackEvent("testimonial_submit_success", {
        has_name_consent: d.consent_publish_name,
        has_org_consent: d.consent_publish_org,
        marketing_ok: d.consent_use_marketing,
      });
      setSubmitted(true);
      toast.success("Thank you — your testimonial and release are on file.");
    } catch (err) {
      console.error("[TestimonialIntake] submit failed", err);
      trackEvent("testimonial_submit_error");
      void reportTestimonialFailure({
        reason: `Save failed: ${(err as Error)?.message ?? "unknown error"}`,
        fields: [],
        email: parsed.data.email,
      });
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Client Testimonial & Release — Dr. Sully Speaks"
        description="Submit a consented client testimonial with a release effective date. Reviewed before any publication."
        path="/testimonial-intake"
      />
      <SiteNav />

      <main className="container mx-auto max-w-3xl px-4 py-16">
        <header className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary">
            <Quote className="h-4 w-4" aria-hidden />
            Testimonial & written release
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight md:text-5xl">
            Share a Client Testimonial
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Send a quote you'd like Dr. Sully's team to consider for publishing. You choose
            exactly what may be shown — quote only, first name, full name, and/or your
            organization — and when the release starts.
          </p>
        </header>

        {submitted ? (
          <section
            role="status"
            aria-live="polite"
            className="rounded-2xl border border-primary/30 bg-card p-10 text-center shadow-lg"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-primary" aria-hidden />
            <h2 className="mt-4 font-display text-3xl font-bold">Thank you.</h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Your testimonial and signed release are logged. Nothing is published until it
              passes review — and never before the release effective date you chose.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="outline">
                <a href="/">Back to homepage</a>
              </Button>
              <Button asChild>
                <a href="/reviews">See published reviews</a>
              </Button>
            </div>
          </section>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            className="space-y-6 rounded-2xl border bg-card p-6 shadow-sm md:p-10"
          >
            {/* honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="full_name">Your name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  required
                  aria-invalid={!!errors.full_name}
                  onBlur={() => clearError("full_name")}
                  className={errors.full_name ? "border-destructive" : ""}
                />
                {errors.full_name && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.full_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  aria-invalid={!!errors.email}
                  onBlur={() => clearError("email")}
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="role_title">Role / title (optional)</Label>
                <Input
                  id="role_title"
                  name="role_title"
                  placeholder="e.g. Special Ed Director"
                  onBlur={() => clearError("role_title")}
                />
              </div>
              <div>
                <Label htmlFor="organization">Organization (optional)</Label>
                <Input
                  id="organization"
                  name="organization"
                  placeholder="e.g. Northside ISD"
                  onBlur={() => clearError("organization")}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="quote">Your quote *</Label>
              <Textarea
                id="quote"
                name="quote"
                required
                rows={5}
                maxLength={1200}
                placeholder="A few sentences we may use as a testimonial."
                aria-invalid={!!errors.quote}
                onBlur={() => clearError("quote")}
                className={errors.quote ? "border-destructive" : ""}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                20–1,200 characters. This is what may be published.
              </p>
              {errors.quote && (
                <p role="alert" className="mt-1 text-sm text-destructive">
                  {errors.quote}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="context">Additional context (optional, not published)</Label>
              <Textarea
                id="context"
                name="context"
                rows={4}
                maxLength={4000}
                placeholder="Background for our team — e.g. which engagement or outcome this refers to."
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="rating">Rating (optional)</Label>
                <select
                  id="rating"
                  name="rating"
                  defaultValue=""
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">— None —</option>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n} / 5
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="release_effective_date">Release starts *</Label>
                <Input
                  id="release_effective_date"
                  name="release_effective_date"
                  type="date"
                  required
                  min={todayISO()}
                  defaultValue={todayISO()}
                  aria-invalid={!!errors.release_effective_date}
                  onBlur={() => clearError("release_effective_date")}
                  className={errors.release_effective_date ? "border-destructive" : ""}
                />
                {errors.release_effective_date && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.release_effective_date}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="release_expires_at">Release ends (optional)</Label>
                <Input
                  id="release_expires_at"
                  name="release_expires_at"
                  type="date"
                  aria-invalid={!!errors.release_expires_at}
                  onBlur={() => clearError("release_expires_at")}
                  className={errors.release_expires_at ? "border-destructive" : ""}
                />
                {errors.release_expires_at && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.release_expires_at}
                  </p>
                )}
              </div>
            </div>

            <fieldset className="space-y-3 rounded-xl border bg-background/60 p-5">
              <legend className="flex items-center gap-2 px-1 text-sm font-semibold">
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
                Consent & release
              </legend>

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_publish_quote" name="consent_publish_quote" />
                <span>
                  <strong>Required.</strong> I grant Dr. Sully Speaks a non-exclusive,
                  royalty-free license to publish, reproduce, and edit my quote for length
                  and clarity on drsullyspeaks.com and related channels, starting on the
                  release date above.
                </span>
              </label>
              {errors.consent_publish_quote && (
                <p role="alert" className="text-sm text-destructive">
                  {errors.consent_publish_quote}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_publish_name" name="consent_publish_name" />
                <span>You may publish my <strong>full name</strong> with the quote. (If unchecked, quote will run anonymously or with first-name-and-last-initial only.)</span>
              </label>

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_publish_org" name="consent_publish_org" />
                <span>You may publish my <strong>role and organization</strong> with the quote.</span>
              </label>

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_use_marketing" name="consent_use_marketing" />
                <span>You may also use the quote in <strong>marketing materials</strong> (social posts, sales sheets, decks, ads).</span>
              </label>
            </fieldset>

            <div>
              <Label htmlFor="signature_name">Typed signature *</Label>
              <Input
                id="signature_name"
                name="signature_name"
                required
                placeholder="Type your full legal name"
                aria-invalid={!!errors.signature_name}
                onBlur={() => clearError("signature_name")}
                className={errors.signature_name ? "border-destructive" : ""}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Typing your name here acts as your electronic signature confirming the
                release above. A timestamp is recorded automatically.
              </p>
              {errors.signature_name && (
                <p role="alert" className="mt-1 text-sm text-destructive">
                  {errors.signature_name}
                </p>
              )}
            </div>

            <Button type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Submitting…
                </>
              ) : (
                "Submit testimonial & release"
              )}
            </Button>

            <p className="text-xs text-muted-foreground">
              Submissions are reviewed before any publication. You can withdraw consent at
              any time by emailing us — see our{" "}
              <a href="/legal" className="underline">privacy notice</a>.
            </p>
          </form>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
